/**
 * Technology stack / Areas — list, add, edit, delete areas (workspace-scoped).
 * Use areas to scope capabilities (e.g. DevOps in Product One vs Product Two).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Plus, Pencil, Trash2, X } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { PlanningListSkeleton } from '../../components/planning/PlanningListSkeleton';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { DEMO_WORKSPACE_ID } from '../../lib/tenantContext';
import {
  listAreas,
  createArea,
  updateArea,
  deleteArea,
} from '../../lib/areaService';
import type { Area } from '../../types/capabilities';

export default function PlanningAreas() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const workspaceId = getCurrentWorkspaceId() ?? DEMO_WORKSPACE_ID;

  const load = () => {
    if (!workspaceId) {
      setAreas([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listAreas(workspaceId).then(({ data, error: err }) => {
      setAreas(data);
      setError(err ?? null);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, [workspaceId]);

  const openCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormDescription('');
    setFormSortOrder(areas.length);
    setShowForm(true);
    setSaveError(null);
  };

  const openEdit = (a: Area) => {
    setEditingId(a.id);
    setFormName(a.name);
    setFormDescription(a.description ?? '');
    setFormSortOrder(a.sort_order);
    setShowForm(true);
    setSaveError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId || !formName.trim()) return;
    setSaving(true);
    setSaveError(null);
    if (editingId) {
      const { error: err } = await updateArea(editingId, {
        name: formName.trim(),
        description: formDescription.trim() || null,
        sort_order: formSortOrder,
      });
      if (err) setSaveError(err);
      else {
        closeForm();
        load();
      }
    } else {
      const { error: err } = await createArea({
        workspace_id: workspaceId,
        name: formName.trim(),
        description: formDescription.trim() || null,
        sort_order: formSortOrder,
      });
      if (err) setSaveError(err);
      else {
        closeForm();
        load();
      }
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this area? Capability ratings scoped to it will become general (no area).')) return;
    const { error: err } = await deleteArea(id);
    if (err) setError(err);
    else load();
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        icon={<Box className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50 dark:bg-[var(--surface)]"
        title="Technology stack / Areas"
        subtitle="Define areas (e.g. Product One, Product Two) so you can attach capabilities per area. Someone can have DevOps in one product but not another."
      />

      <p className="text-sm text-[var(--text-sec)] mb-4">
        Areas are scoped to your current workspace. To switch workspace, go to <Link to="/prioritization" className="text-primary-600 hover:underline">Prioritization</Link> and use the workspace selector in the left sidebar.
      </p>

      {workspaceId && (
        <>
          {error && (
            <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
              {error}
            </div>
          )}
          {loading ? (
            <PlanningListSkeleton className="min-h-[240px]" />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                >
                  <Plus className="h-4 w-4" />
                  Add area
                </button>
              </div>
              {areas.length === 0 && !showForm ? (
                <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8 text-center text-[var(--text-sec)]">
                  <p className="font-medium">No areas yet</p>
                  <p className="text-sm mt-1">Add an area (e.g. Product One, Backend Services) so you can scope capabilities per area in People & capabilities.</p>
                  <button
                    type="button"
                    onClick={openCreate}
                    className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                  >
                    <Plus className="h-4 w-4" />
                    Add area
                  </button>
                </div>
              ) : (
                <ul className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)]">
                  {areas.map((a) => (
                    <li key={a.id} className="px-4 py-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-[var(--text-pri)]">{a.name}</p>
                        {a.description && (
                          <p className="text-sm text-[var(--text-ter)] mt-0.5">{a.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          className="p-1.5 rounded text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)]"
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(a.id)}
                          className="p-1.5 rounded text-[var(--text-ter)] hover:bg-[var(--coral-dim)] hover:text-[var(--coral)]"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" role="dialog" aria-label={editingId ? 'Edit area' : 'Add area'}>
              <div className="bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-[var(--text-pri)]">{editingId ? 'Edit area' : 'Add area'}</h2>
                  <button type="button" onClick={closeForm} className="p-1 rounded hover:bg-[var(--hover-tint)]" aria-label="Close">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g. Product One, Backend Services"
                      className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
                    <textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      rows={2}
                      className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Sort order</label>
                    <input
                      type="number"
                      min={0}
                      value={formSortOrder}
                      onChange={(e) => setFormSortOrder(Number(e.target.value) || 0)}
                      className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                    />
                  </div>
                  {saveError && <p className="text-sm text-[var(--coral)]">{saveError}</p>}
                  <div className="flex gap-2 justify-end pt-2">
                    <button type="button" onClick={closeForm} className="px-3 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">
                      Cancel
                    </button>
                    <button type="submit" disabled={saving || !formName.trim()} className="px-3 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
                      {saving ? 'Saving…' : editingId ? 'Save' : 'Add'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
